"use client"

import { useId, useMemo, useState } from "react"
import { Household } from "@/schema"
import formatPhone, { formatPhoneTelHref } from "@/utils/format-phone"
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton } from "@ui/shad"
import { Lock } from "lucide-react"
import { toast } from "sonner"

import { useFetchCurrentClientIntegrations } from "@/hooks/api/current-client-integrations"
import { useFetchDialpadIntegrations } from "@/hooks/api/dialpad-integrations"
import { useElevateIntegrations } from "@/hooks/api/elevate-integrations/queries"
import { useFetchGoToIntegrations } from "@/hooks/api/go-to-integrations"
import { useFetchMyRepChatIntegrations } from "@/hooks/api/my-rep-chat-integrations"
import { useFetchRingCentralIntegrations } from "@/hooks/api/ring-central-integrations"
import { useInitiateRingout } from "@/hooks/api/ringouts/mutations"
import { useFetchZoomPhoneIntegrations } from "@/hooks/api/zoom-phone-integrations"
import { SimpleFormDialog } from "@/components/dialogs"

interface RingoutCallDialogProps {
  household?: Household
  onClose: () => void
  preSelectedPhone?: string
  preSelectedExtension?: string | null
  preSelectedLabel?: string
}

interface PhoneOption {
  key: string
  label: string
  phone: string
  extension?: string | null
}

interface InboxOption {
  phone: string
  integrationId: string
  integrationName: string
  integrationType: string
  displayDetail?: string
  isTelDial?: boolean
  dialUrlScheme?: string
}

export function RingoutCallDialog({
  onClose,
  household,
  preSelectedPhone,
  preSelectedExtension,
  preSelectedLabel,
}: RingoutCallDialogProps) {
  const [selectedPhoneKey, setSelectedPhoneKey] = useState<string | null>(null)
  const [selectedInboxKey, setSelectedInboxKey] = useState<string | null>(null)
  const { mutateAsync: initiateRingout, isPending } = useInitiateRingout()
  const closeDialog = onClose

  const { data: ccIntegrations, isLoading: ccLoading } = useFetchCurrentClientIntegrations()
  const { data: rcIntegrations, isLoading: rcLoading } = useFetchRingCentralIntegrations()
  const { data: mrcIntegrations, isLoading: mrcLoading } = useFetchMyRepChatIntegrations()
  const { data: dpIntegrations, isLoading: dpLoading } = useFetchDialpadIntegrations()
  const { data: goToIntegrations, isLoading: goToLoading } = useFetchGoToIntegrations()
  const { data: zoomIntegrations, isLoading: zoomLoading } = useFetchZoomPhoneIntegrations()
  const { data: elevateIntegrations, isLoading: elevateLoading } = useElevateIntegrations()
  const isLoading = elevateLoading || ccLoading || rcLoading || mrcLoading || dpLoading || goToLoading || zoomLoading

  const phoneOptions = useMemo(() => {
    const options: PhoneOption[] = []
    const people = household?.people ?? []

    for (const person of people) {
      for (const phone of person.phone_numbers ?? []) {
        const displayPhone = `${formatPhone(phone.unformatted_phone)}${phone.extension ? ` ext ${phone.extension}` : ""}`
        options.push({
          key: `${person.id}:${phone.id}`,
          label: `${person.full_name} — ${displayPhone} (${phone.phone_type})`,
          phone: phone.unformatted_phone,
          extension: phone.extension,
        })
      }
    }

    return options
  }, [household?.people])

  const inboxOptions = useMemo(() => {
    const options: InboxOption[] = []

    for (const i of (ccIntegrations ?? []).filter((i) => i.supports_ringout)) {
      for (const phone of i.inboxes) {
        options.push({
          phone,
          integrationId: i.id,
          integrationName: i.account_name ?? "CurrentClient",
          integrationType: "CurrentClientIntegration",
        })
      }
    }

    for (const i of (rcIntegrations ?? []).filter((i) => i.is_current_user && i.configured && i.connected)) {
      options.push({
        phone: i.forwarding_number ?? "",
        integrationId: i.id,
        integrationName: i.user?.full_name ?? "RingCentral",
        integrationType: "RingCentralIntegration",
        isTelDial: !i.supports_ringout,
      })
    }

    for (const i of (mrcIntegrations ?? []).filter((i) => i.supports_ringout)) {
      if (i.phone_number) {
        options.push({
          phone: i.phone_number,
          integrationId: i.id,
          integrationName: "MyRepChat",
          integrationType: "MyRepChatIntegration",
        })
      }
    }

    for (const i of (dpIntegrations ?? []).filter((i) => i.supports_ringout)) {
      for (const phone of i.phone_numbers ?? []) {
        options.push({
          phone,
          integrationId: i.id,
          integrationName: i.account_name ?? i.email ?? "Dialpad",
          integrationType: "DialpadIntegration",
        })
      }
    }

    for (const i of (goToIntegrations ?? []).filter(
      (i) => i.is_current_user && i.supports_ringout && i.phone_numbers.length > 0
    )) {
      options.push({
        phone: i.phone_numbers[0] ?? "",
        integrationId: i.id,
        integrationName: i.account_name ?? "GoTo Connect",
        integrationType: "GoToIntegration",
        displayDetail: i.line_name ?? "Assigned line",
      })
    }

    for (const i of (zoomIntegrations ?? []).filter((i) => i.connected)) {
      for (const phone of i.display_phone_numbers) {
        options.push({
          phone,
          integrationId: i.id,
          integrationName: i.display_name ?? "Zoom Phone",
          integrationType: "ZoomPhoneIntegration",
          isTelDial: true,
          dialUrlScheme: "zoomphonecall",
        })
      }
    }

    for (const integration of (elevateIntegrations ?? []).filter(
      (item) => item.is_current_user && item.supports_ringout
    )) {
      options.push({
        phone: integration.caller_id?.phoneNumber ?? "",
        integrationId: integration.id,
        integrationName: integration.display_name ?? "Elevate",
        integrationType: "ElevateIntegration",
        displayDetail:
          integration.devices?.find((device) => device.id === integration.calling_device_id)?.name ?? "Elevate",
      })
    }
    return options
  }, [
    elevateIntegrations,
    ccIntegrations,
    rcIntegrations,
    mrcIntegrations,
    dpIntegrations,
    goToIntegrations,
    zoomIntegrations,
  ])

  const inboxKey = (opt: InboxOption) =>
    opt.isTelDial && !opt.dialUrlScheme
      ? `tel:${opt.integrationType}:${opt.integrationId}`
      : `${opt.integrationType}:${opt.integrationId}:${opt.phone}`
  const effectiveInboxKey = selectedInboxKey ?? (inboxOptions[0] ? inboxKey(inboxOptions[0]) : "")
  const effectiveInbox = inboxOptions.find((o) => inboxKey(o) === effectiveInboxKey) ?? inboxOptions[0]
  const preselectedPhoneOption = preSelectedPhone
    ? phoneOptions.find(
        (option) => option.phone === preSelectedPhone && (option.extension ?? null) === (preSelectedExtension ?? null)
      )
    : null
  const effectivePhoneKey = selectedPhoneKey ?? preselectedPhoneOption?.key ?? phoneOptions[0]?.key ?? ""
  const effectivePhoneOption = phoneOptions.find((option) => option.key === effectivePhoneKey) ?? phoneOptions[0]
  const effectivePhone = effectivePhoneOption?.phone ?? preSelectedPhone ?? ""
  const effectiveExtension = effectivePhoneOption?.extension ?? preSelectedExtension
  const canSubmit = Boolean(
    effectivePhone &&
      effectiveInbox &&
      (effectiveInbox.isTelDial || effectiveInbox.phone || effectiveInbox.integrationType === "ElevateIntegration") &&
      !isPending &&
      !isLoading
  )

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (!canSubmit) return

    if (effectiveInbox.isTelDial) {
      if (effectiveInbox.dialUrlScheme) {
        window.open(
          `${effectiveInbox.dialUrlScheme}://${effectivePhone}?callerid=${encodeURIComponent(effectiveInbox.phone)}`,
          "_self"
        )
        closeDialog()
      } else {
        window.open(formatPhoneTelHref(effectivePhone, effectiveExtension), "_self")
      }
      return
    }

    await initiateRingout({
      integrationId: effectiveInbox!.integrationId,
      integrationType: effectiveInbox!.integrationType,
      toPhone: effectivePhone,
      inboxPhone: effectiveInbox!.phone,
    })

    toast.success(effectiveInbox.integrationType === "ElevateIntegration" ? "Call requested" : "Call initiated")
    closeDialog()
  }

  const formId = useId()
  if (isLoading) {
    return (
      <SimpleFormDialog
        isolateEvents
        open
        onOpenChange={(open) => !open && closeDialog()}
        title="Initiate call"
        action={{ label: "Call", form: formId, disabled: true }}
      >
        <div className="space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      </SimpleFormDialog>
    )
  }

  return (
    <SimpleFormDialog
      isolateEvents
      open
      onOpenChange={(open) => !open && closeDialog()}
      title="Initiate call"
      action={{ label: "Call", form: formId, disabled: !canSubmit, pending: isPending }}
      cancel={{ onClick: closeDialog, disabled: isPending }}
    >
      <form id={formId} onSubmit={handleSubmit}>
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm leading-tight">
            {effectiveInbox?.dialUrlScheme === "zoomphonecall"
              ? "Select a name and number to call. The Zoom app will open and place the call."
              : effectiveInbox?.isTelDial
                ? "Select a name and number to call. Your phone dialer will open with the selected number."
                : "Select a name and number to call. Your phone will ring first, then bridge to the individual selected."}
          </p>

          <div className="space-y-2">
            <Label>Individual to call</Label>
            {preSelectedPhone ? (
              <div className="border-input bg-muted flex h-9 w-full items-center justify-between rounded-md border px-3 py-2 text-sm shadow-xs">
                <span className="line-clamp-1">
                  {preSelectedLabel ??
                    phoneOptions.find((o) => o.phone === preSelectedPhone)?.label ??
                    formatPhone(preSelectedPhone)}
                </span>
                <Lock className="-mr-0.5 ml-1 size-3.5 opacity-50" />
              </div>
            ) : (
              <Select value={effectivePhoneKey} onValueChange={setSelectedPhoneKey} disabled={isPending}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a phone number" />
                </SelectTrigger>
                <SelectContent>
                  {phoneOptions.map((option) => (
                    <SelectItem key={option.key} value={option.key}>
                      {/* Radix portals this label into the trigger; keep a stable node if browser tools rewrite its text. */}
                      <span>{option.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {inboxOptions.length === 1 ? (
            <p className="text-sm">
              Calling from{" "}
              <span className="font-semibold">
                {inboxOptions[0].integrationName} —{" "}
                {inboxOptions[0].displayDetail ??
                  (inboxOptions[0].phone ? formatPhone(inboxOptions[0].phone) : "RingCentral")}
              </span>
            </p>
          ) : inboxOptions.length > 1 ? (
            <div className="space-y-2">
              <Label>Call from</Label>
              <Select value={effectiveInboxKey} onValueChange={setSelectedInboxKey} disabled={isPending}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an inbox number" />
                </SelectTrigger>
                <SelectContent>
                  {inboxOptions.map((option) => (
                    <SelectItem key={inboxKey(option)} value={inboxKey(option)}>
                      <span>
                        {option.displayDetail
                          ? `${option.integrationName} — ${option.displayDetail}`
                          : option.phone
                            ? `${option.integrationName} — ${formatPhone(option.phone)}`
                            : `${option.integrationName} — RingCentral`}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>
      </form>
    </SimpleFormDialog>
  )
}
